import { useEffect, useRef, useState, useCallback } from "react";
import ReactTextareaAutocomplete from "@webscopeio/react-textarea-autocomplete";
import "@webscopeio/react-textarea-autocomplete/style.css";
import useAxiosInstance from "../../helpers/useAxiosInstance";
import { useDebounce } from "./useDebounce";

/* === Placeholder groups === */
const PLACEHOLDER_GROUPS = [
  {
    label: "Client",
    items: [
      { key: "{{firstname}}", desc: "Client First Name", field: "first_name" },
      { key: "{{lastname}}", desc: "Client Last Name", field: "last_name" },
      { key: "{{budget}}", desc: "Client Budget", field: "budget" },
      { key: "{{desired_location}}", desc: "Client Desired Location", field: "desired_location" },
      { key: "{{move_in_date}}", desc: "Client Move-In Date", field: "move_in_date" },
    ],
  },
  {
    label: "Property",
    items: [
      { key: "{{name}}", desc: "Property Name", field: "name" },
      { key: "{{market}}", desc: "Property Market", field: "market" },
      { key: "{{submarket}}", desc: "Property Submarket", field: "submarket" },
      { key: "{{street_address}}", desc: "Property Street Address", field: "street_address" },
      { key: "{{city}}", desc: "Property City", field: "city" },
      { key: "{{state}}", desc: "Property State", field: "state" },
      { key: "{{zip_code}}", desc: "Property Zip Code", field: "zip_code" },
    ],
  },
];

const ALL_PLACEHOLDERS = PLACEHOLDER_GROUPS.flatMap((g) => g.items);

/* === Helpers === */
function resolveFieldValue(field, clientObj = {}, propertyObj = {}) {
  if (!field) return "";
  const variants = [
    field,
    field.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
    field.replace(/_/g, ""),
    field.toLowerCase(),
  ];
  for (const k of variants) if (clientObj?.[k] != null) return clientObj[k];
  for (const k of variants) if (propertyObj?.[k] != null) return propertyObj[k];
  const plain = field.replace(/[^a-zA-Z0-9]/g, "");
  if (clientObj?.[plain] != null) return clientObj[plain];
  if (propertyObj?.[plain] != null) return propertyObj[plain];
  return "";
}
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function substitutePlaceholdersWithMap(text, clientObj = {}, propertyObj = {}) {
  //if (!text) return "";
  let out = text;
  PLACEHOLDER_GROUPS.forEach((g) =>
    g.items.forEach((ph) => {
      const val = resolveFieldValue(ph.field, clientObj, propertyObj);
      out = out.replace(new RegExp(escapeRegex(ph.key), "gi"), val ?? "");
    })
  );
  return out;
}

/* === ReplyPopup Component === */
export default function ReplyPopup({
  isOpen,
  onClose,
  defaultText,
  phoneNumber,
  onSend,
  onGenerateReply,
  showSuggestion = true,
  client = { firstname: "", lastname: "", budget: "" },
  propertyApiUrl = "/properties/autocomplete",
}) {
  const [customText, setCustomText] = useState(defaultText || "");
  const [suggestions, setSuggestions] = useState([]);
  const [mode, setMode] = useState("custom");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generateLocked, setGenerateLocked] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(
    Object.fromEntries(PLACEHOLDER_GROUPS.map((g) => [g.label, true]))
  );

  const [propertyQuery, setPropertyQuery] = useState("");
  const [propertyResults, setPropertyResults] = useState([]);
  const [propertyPage, setPropertyPage] = useState(1);
  const [propertyHasMore, setPropertyHasMore] = useState(false);
  const [propertyLoading, setPropertyLoading] = useState(false);

  const propertyDropdownRef = useRef(null);
  const rtaRef = useRef(null);
  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);
  const contextMenuRef = useRef(null);

  const axiosInstance = useAxiosInstance();

  /* Reset on open */
  useEffect(() => {
    if (isOpen) {
      setCustomText(defaultText || "");
      setSuggestions([]);
      setMode("custom");
      setLoading(false);
      setError(null);
      setGenerateLocked(false);
      setSelectedPropertyId("");
      setSelectedProperty(null)
      setContextMenu(null);
      setPropertyQuery("");
      setPropertyResults([]);
      setPropertyPage(1);
      setPropertyHasMore(false);
    }
  }, [isOpen, defaultText]);

  /* Outside click closes context menu */
  // useEffect(() => {
  //   const handleClickOutside = (e) => {
  //     if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
  //       setContextMenu(null);
  //     }
  //   };
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);
  useEffect(() => {
  const handleClickOutside = (e) => {
    console.log("Outside")
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
      setContextMenu(null);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  /* Keyboard navigation in context menu */
  const [focusedIndex, setFocusedIndex] = useState(-1);

  //const selectedProperty = propertyResults.find((p) => p.id === selectedPropertyId);

  const treeItems = PLACEHOLDER_GROUPS.flatMap((g) =>
    expandedGroups[g.label]
      ? g.items.filter((ph) => resolveFieldValue(ph.field, client, selectedProperty) !== "")
      : []
  );
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!contextMenu) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % treeItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + treeItems.length) % treeItems.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (treeItems[focusedIndex]) insertPlaceholderAtCursor(treeItems[focusedIndex].key);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setContextMenu(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [contextMenu, focusedIndex, treeItems]);

  const substitutePlaceholders = (t) =>
    substitutePlaceholdersWithMap(t, client, selectedProperty);

  /* Generate reply */
  const handleGenerate = async () => {
    setGenerateLocked(true);
    setLoading(true);
    setError(null);
    try {
      const list = await onGenerateReply(phoneNumber);
      setSuggestions(list || []);
      setMode("suggestions");
      if (!list?.length) setError("No suggestions generated.");
    } catch (err) {
      setSuggestions([]);
      setMode("custom");
      setError(err?.message || "Failed to generate suggestions.");
    } finally {
      setLoading(false);
    }
  };
  const handleSelectSuggestion = (t) => {
    setCustomText(t ?? "");
    setMode("custom");
  };

  /* Send message */
  const handleSend = async () => {
    const trimmed = customText.trim();
    // if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      await onSend({ phone: phoneNumber, message: substitutePlaceholders(trimmed) });
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to send.");
    } finally {
      setLoading(false);
    }
  };

  /* Insert placeholder at cursor */
  const insertPlaceholderAtCursor = (phKey) => {
    const rta = textareaRef.current;
    if (!rta) return;
    const start = rta.selectionStart;
    const end = rta.selectionEnd;
    const before = customText.slice(0, start);
    const after = customText.slice(end);
    const newText = before + phKey + after;
    setCustomText(newText);
    const newPos = before.length + phKey.length;
    setTimeout(() => {
      rta.focus();
      rta.setSelectionRange(newPos, newPos);
    }, 0);
    setContextMenu(null);
    setFocusedIndex(-1);
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };
  const toggleGroup = (l) => setExpandedGroups((prev) => ({ ...prev, [l]: !prev[l] }));

  /* === Property Autocomplete === */
  const fetchProperties = async (query, page = 1) => {
    if (query.length < 3) {
      setPropertyResults([]);
      setPropertyHasMore(false);
      return;
    }
    setPropertyLoading(true);
    try {
      const res = await axiosInstance.get(propertyApiUrl, { params: { q: query, page, limit: 5 } });
      if (page === 1) setPropertyResults(res.data.items);
      else setPropertyResults((prev) => [...prev, ...res.data.items]);
      setPropertyHasMore(res.data.hasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setPropertyLoading(false);
    }
  };
  const debouncedFetch = useDebounce(fetchProperties, 400);
  const handlePropertyInput = (e) => {
    const val = e.target.value;
    setPropertyQuery(val);
    setPropertyPage(1);
    debouncedFetch(val, 1);
  };
  const loadMoreProperties = () => {
    if (!propertyHasMore || propertyLoading) return;
    const nextPage = propertyPage + 1;
    setPropertyPage(nextPage);
    fetchProperties(propertyQuery, nextPage);
  };
  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) loadMoreProperties();
  };
  const handleSelectProperty = (prop) => {
    setSelectedPropertyId(prop.id);
    setPropertyQuery(prop.name);
    setSelectedProperty(prop)
    setPropertyResults([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center text-md" ref={wrapperRef}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">

        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              Send Message {phoneNumber && <span className="text-sm text-gray-500">({phoneNumber})</span>}
            </h2>
            {client && (
              <p className="mt-1 text-sm text-gray-600">
                Client: <span className="font-medium">{client.firstname || ""}</span>{" "}
                {client.lastname || ""} - Budget: <span className="font-medium">{client.budget || ""}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {showSuggestion && (
              <button onClick={handleGenerate} disabled={loading || generateLocked}
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Generating..." : "Generate Reply"}
              </button>
            )}
            <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">✕</button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200 flex justify-between">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Property dropdown */}
        <div className="mb-4 relative">
          <label className="block text-sm mb-1">Select Property</label>
          {/* <input
            type="text"
            value={propertyQuery}
            onChange={handlePropertyInput}
            placeholder="Type property name..."
            className="w-full rounded border px-2 py-1 text-md"
          /> */}
          <div className="relative text-md">
            <input
              type="text"
              value={propertyQuery}
              onChange={handlePropertyInput}
              placeholder="Type property name..."
              className="w-full rounded border px-2 py-1 pr-8"
            />
            {propertyQuery && (
              <button
                onClick={() => {
                  setPropertyQuery("");
                  setSelectedPropertyId("");
                  setPropertyResults([]);
                  setPropertyPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            )}
          </div>
          {propertyResults.length > 0 && (
            <div
              ref={propertyDropdownRef}
              onScroll={handleScroll}
              className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded border bg-white shadow-lg"
            >
              {propertyResults.map((p) => (
                <div
                  key={p.id}
                  onClick={() => handleSelectProperty(p)}
                  className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer"
                >
                  {p.name}
                </div>
              ))}
              {propertyLoading && <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>}
              {!propertyLoading && propertyHasMore && (
                <div
                  onClick={loadMoreProperties}
                  className="px-3 py-2 text-sm text-center text-blue-600 cursor-pointer hover:underline"
                >
                  Load more…
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mode toggle */}
        {showSuggestion && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <button onClick={() => setMode("custom")}
              className={`rounded border px-3 py-2 text-sm ${mode === "custom" ? "border-blue-400 bg-blue-100" : "bg-gray-100"}`}>
              Custom Text
            </button>
            <button onClick={() => setMode("suggestions")} disabled={!suggestions.length}
              className={`rounded border px-3 py-2 text-sm ${mode === "suggestions" ? "border-blue-400 bg-blue-100" : "bg-gray-100"}`}>
              Suggestions
            </button>
          </div>
        )}

        {/* Suggestions / Editor */}
        {mode === "suggestions" && showSuggestion ? (
          <div className="mb-2 space-y-2">
            {suggestions.length ? (
              suggestions.map((s, i) => (
                <button key={i} onClick={() => handleSelectSuggestion(s)}
                  className="block w-full rounded border p-2 text-left hover:bg-blue-50">
                  {substitutePlaceholders(s)}
                </button>
              ))
            ) : (
              <div className="rounded border p-2 text-sm text-gray-600">No suggestions.</div>
            )}
          </div>
        ) : (
          <div className="relative mb-2 text-md">
            <ReactTextareaAutocomplete
              ref={rtaRef}
              innerRef={(instance) => { textareaRef.current = instance; }}
              className="w-full min-h-[140px] rounded border p-3 outline-none"
              style={{
                fontSize: "1rem"
              }}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onContextMenu={handleContextMenu}
              placeholder="Type your message… (use {{firstname}}, {{budget}}, etc. or right click.)"
              loadingComponent={() => <span className="text-gray-400 px-2">Loading…</span>}
              trigger={{
                "{{": {
                  dataProvider: (token) => {
                    const query = token.toLowerCase();
                    if (!query) return ALL_PLACEHOLDERS;
                    return ALL_PLACEHOLDERS.filter((ph) =>
                      ph.key.toLowerCase().includes(query)
                    );
                  },
                  component: ({ entity }) => (
                    <div><span className="font-mono">{entity.key}</span> - {entity.desc}</div>
                  ),
                  output: (entity) => entity.key,
                },
              }}
            />
            {/* Live Preview */}
            <div className="mt-2 rounded border bg-gray-50 p-2 text-sm text-gray-800 whitespace-pre-wrap">
              {substitutePlaceholders(customText) || "Live preview will appear here..."}
            </div>
          </div>
        )}

        {/* Context menu */}
        {contextMenu && (
          <div 
           ref={contextMenuRef}
            className="fixed z-50 w-64 rounded border bg-white shadow-lg max-h-80 overflow-auto text-md"
            style={{ top: contextMenu.y, left: contextMenu.x }}         
            >
            {PLACEHOLDER_GROUPS.map((g) => (
              <div key={g.label} className="border-b last:border-0">
                <button 
                  onClick={() => toggleGroup(g.label)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                  >
                  {expandedGroups[g.label] ? "▼" : "▶"} {g.label}
                </button>
                {console.log("g?.items",g?.items)}
                {expandedGroups[g.label] &&
                  g.items.map((ph) => {
                    const fieldVal = resolveFieldValue(ph.field, client, selectedProperty);
                    if (!fieldVal) return null;
                    return (
                      <div
                        key={ph.key}
                        onClick={() => insertPlaceholderAtCursor(ph.key)}
                        className={`px-6 py-2 text-sm hover:bg-blue-50 cursor-pointer flex justify-between ${
                          focusedIndex === treeItems.findIndex(t => t.key === ph.key) ? "bg-blue-100" : ""
                        }`}
                      >
                        <div><span className="text-sm">{ph.key}</span> - <span className="text-xs text-gray-500">{ph.desc}</span></div>
                        {/* <div className="text-xs text-gray-500">{fieldVal}</div> */}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded border px-4 py-2 text-sm hover:bg-gray-100">Cancel</button>
          <button onClick={handleSend} disabled={loading}
            className="rounded bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50">
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
